import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CaseManagementPreferenceList } from "components/CasesTable/CaseManagementPreferenceList";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { useFetchDelete } from "hooks/fetch/useFetchDelete";
import { useFetchPut } from "hooks/fetch/useFetchPut";

import { styled } from "@mui/material/styles";
import { CaseManagementParamsContext } from "contexts/CaseManagementParamsContext";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { Dialog } from "components/Dialog";

export function ManageTableViewDialog({
  open,
  setOpen,
  preferences,
  refetch,
  setCreateViewOpen
}) {
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { tableState, savedTableView, onSetSavedTableView } = useContext(
    CaseManagementParamsContext
  );

  const [selectedPreferenceId, setSelectedPreferenceId] = useState(null);
  const [preferenceIdToDelete, setPreferenceIdToDelete] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const { loading: updateLoading, putCallback: updatePreference } = useFetchPut(
    `case_management_preferences/${selectedPreferenceId}`,
    updatedPreference => {
      onSetSavedTableView(updatedPreference);
      addSnackbarToQueue({
        message: "Table view updated.",
        severity: "success"
      });
      refetch();
    },
    () => {
      addSnackbarToQueue({
        message: "Table view failed to update.",
        severity: "error"
      });
    }
  );

  const {
    loading: deleteLoading,
    deleteCallback: deletePreference
  } = useFetchDelete(
    `case_management_preferences/${preferenceIdToDelete}`,
    () => {
      addSnackbarToQueue({
        message: "Table view deleted.",
        severity: "success"
      });
      refetch();
      setPreferenceIdToDelete(null);
    },
    () => {
      addSnackbarToQueue({
        message: "Table view failed to delete.",
        severity: "error"
      });
      setPreferenceIdToDelete(null);
    }
  );

  const onDeleteTableView = pref => {
    setPreferenceIdToDelete(pref.id);

    if (pref.id === savedTableView?.id) onSetSavedTableView(null);

    setSelectedPreferenceId(null);
  };

  useEffect(() => {
    if (preferenceIdToDelete) deletePreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceIdToDelete]);

  const onUpdateTableView = () => {
    const preferenceToUpdate = preferences.find(
      ({ id }) => id === selectedPreferenceId
    );

    const params = {
      filters: tableState.filters,
      sortBy: tableState.sortBy,
      hiddenColumns: tableState.hiddenColumns,
      columnOrder: tableState.columnOrder,
      groupBy: tableState.groupBy
    };

    setConfirmationOpen(false);
    setSelectedPreferenceId(null);

    updatePreference({
      params,
      name: preferenceToUpdate.name,
      id: preferenceToUpdate.id
    });
  };

  const onTableViewSelected = useCallback(
    prefId => {
      if (selectedPreferenceId === prefId) {
        setSelectedPreferenceId(null);
      } else {
        setSelectedPreferenceId(prefId);
      }
    },
    [selectedPreferenceId]
  );

  const onOverWriteSelectedTable = () => {
    if (tableState.sortBy.length === 0 || tableState.filters.length === 0) {
      addSnackbarToQueue({
        message:
          "Please set a filter, sort or custom column to save your table view.",
        severity: "error"
      });
      return;
    }
    setConfirmationOpen(true);
  };

  const onClose = () => {
    setSelectedPreferenceId(null);
    setOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        title="Manage Table Views"
        size="medium"
        actionsDisabled={deleteLoading || updateLoading}
        confirmText="Overwrite selected table"
        cancelText="create new table"
        onClose={onClose}
        onConfirm={onOverWriteSelectedTable}
        confirmDisabled={!selectedPreferenceId}
        onCancel={() => setCreateViewOpen(true)}
      >
        <DialogSubtitle>
          Delete table views or overwrite/create new table views with your
          current preferences.
        </DialogSubtitle>
        <CaseManagementPreferenceList
          preferences={preferences.filter(
            ({ id }) => id !== savedTableView?.id
          )}
          selectedPreferenceId={selectedPreferenceId}
          onTableViewSelected={onTableViewSelected}
          onDeleteTableView={onDeleteTableView}
        />
      </Dialog>
      <ConfirmationDialog
        open={confirmationOpen}
        title="Are you sure you want to overwrite this table view?"
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => onUpdateTableView()}
      />
    </>
  );
}

ManageTableViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  preferences: PropTypes.array.isRequired,
  refetch: PropTypes.func.isRequired,
  setCreateViewOpen: PropTypes.func
};

const DialogSubtitle = styled("span")`
  font-size: 12px;
`;
